/**
 * InsightPro™ — Legal Page  v1.0
 * Route: /legal (public, no auth required)
 * Contains: Privacy Policy, POPIA Compliance Statement, Terms of Use
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const EFFECTIVE_DATE = '15 March 2026';
const COMPANY = 'InsightPro (Pty) Ltd';
const CITY = 'Durban, KwaZulu-Natal, South Africa';
const EMAIL = 'info@insightpro.co.za';
const WEBSITE = 'insightpro.co.za';

const css = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Instrument+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=JetBrains+Mono:wght@300;400;500&display=swap');

.legal-root *,.legal-root *::before,.legal-root *::after{box-sizing:border-box;margin:0;padding:0}
.legal-root{
  --void:#05050a;--void2:#090910;
  --gold:#c4922a;--gold-l:#dba93a;
  --gold-b:rgba(196,146,42,0.22);
  --t1:#eeeef8;--t2:#9898b8;--t3:#50506a;
  --border:rgba(255,255,255,0.055);
  --fd:'Syne',sans-serif;
  --fb:'Instrument Sans',sans-serif;
  --fm:'JetBrains Mono',monospace;
  font-family:var(--fb);
  background:var(--void);
  color:var(--t1);
  min-height:100vh;
}
.legal-nav{
  position:sticky;top:0;z-index:100;
  display:flex;align-items:center;justify-content:space-between;
  padding:0 52px;height:64px;
  background:rgba(5,5,10,0.95);
  backdrop-filter:blur(24px);
  border-bottom:1px solid var(--border);
}
.legal-logo{display:flex;align-items:center;gap:12px;cursor:pointer;text-decoration:none}
.legal-logo-mark{
  width:30px;height:30px;
  background:linear-gradient(135deg,var(--gold-l),var(--gold));
  clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);
  display:flex;align-items:center;justify-content:center;
  flex-shrink:0;
}
.legal-logo-mark span{font-family:var(--fd);font-weight:700;font-size:9px;color:#05050a}
.legal-brand{font-family:var(--fd);font-size:15px;font-weight:700;color:var(--t1);letter-spacing:-0.02em}
.legal-brand sup{font-size:8px;color:var(--gold);font-family:var(--fb)}
.legal-nav-back{
  font-family:var(--fm);font-size:10px;letter-spacing:0.12em;text-transform:uppercase;
  color:var(--t3);cursor:pointer;background:none;border:none;
  transition:color 0.18s;
}
.legal-nav-back:hover{color:var(--t1)}

.legal-body{max-width:820px;margin:0 auto;padding:60px 32px 100px}

.legal-tabs{
  display:flex;gap:4px;
  border-bottom:1px solid var(--border);
  margin-bottom:48px;
}
.legal-tab{
  font-family:var(--fm);font-size:10px;letter-spacing:0.12em;text-transform:uppercase;
  padding:10px 20px;cursor:pointer;background:none;border:none;
  color:var(--t3);border-bottom:2px solid transparent;margin-bottom:-1px;
  transition:color 0.18s;
}
.legal-tab.active{color:var(--gold);border-bottom-color:var(--gold)}
.legal-tab:hover{color:var(--t1)}

.legal-hero{margin-bottom:48px}
.legal-eyebrow{
  font-family:var(--fm);font-size:9px;letter-spacing:0.22em;text-transform:uppercase;
  color:var(--gold);margin-bottom:12px;
}
.legal-title{
  font-family:var(--fd);font-size:32px;font-weight:800;
  letter-spacing:-0.03em;color:var(--t1);margin-bottom:12px;line-height:1.1;
}
.legal-meta{
  font-family:var(--fm);font-size:10px;color:var(--t3);
  letter-spacing:0.08em;
}
.legal-divider{
  height:1px;background:var(--border);margin:40px 0;
}
.legal-section{margin-bottom:36px}
.legal-h2{
  font-family:var(--fd);font-size:18px;font-weight:700;
  color:var(--t1);letter-spacing:-0.02em;margin-bottom:14px;
  padding-top:8px;
}
.legal-h3{
  font-family:var(--fd);font-size:14px;font-weight:600;
  color:var(--gold-l);margin-bottom:10px;margin-top:18px;
}
.legal-p{
  font-size:14px;color:var(--t2);line-height:1.75;
  font-weight:300;margin-bottom:12px;
}
.legal-p strong{color:var(--t1);font-weight:500}
.legal-ul{
  margin:0 0 14px 0;padding-left:20px;
  display:flex;flex-direction:column;gap:6px;
}
.legal-ul li{
  font-size:14px;color:var(--t2);line-height:1.65;font-weight:300;
}
.legal-ul li::marker{color:var(--gold)}
.legal-highlight{
  background:rgba(196,146,42,0.06);
  border:1px solid var(--gold-b);
  border-left:3px solid var(--gold);
  padding:16px 20px;margin:20px 0;border-radius:2px;
}
.legal-highlight p{margin-bottom:0}
.legal-contact-box{
  background:rgba(255,255,255,0.03);
  border:1px solid var(--border);
  padding:24px 28px;margin-top:32px;border-radius:4px;
}
.legal-contact-label{
  font-family:var(--fm);font-size:9px;letter-spacing:0.18em;
  text-transform:uppercase;color:var(--t3);margin-bottom:12px;
}
.legal-contact-line{
  font-size:14px;color:var(--t2);margin-bottom:6px;font-weight:300;
}
.legal-contact-line strong{color:var(--t1);font-weight:500}
.legal-contact-line a{color:var(--gold);text-decoration:none}

.legal-footer{
  border-top:1px solid var(--border);
  padding:32px 52px;
  display:flex;align-items:center;justify-content:space-between;
  flex-wrap:wrap;gap:12px;
}
.legal-footer-copy{
  font-family:var(--fm);font-size:10px;color:var(--t3);letter-spacing:0.08em;
}

@media(max-width:768px){
  .legal-nav{padding:0 20px}
  .legal-body{padding:40px 20px 80px}
  .legal-title{font-size:24px}
  .legal-footer{padding:24px 20px}
}
`;

// ── Section components ────────────────────────────────────────
function H2({ children }) {
  return <div className="legal-h2">{children}</div>;
}
function H3({ children }) {
  return <div className="legal-h3">{children}</div>;
}
function P({ children }) {
  return <p className="legal-p">{children}</p>;
}
function UL({ items }) {
  return (
    <ul className="legal-ul">
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  );
}
function Divider() {
  return <div className="legal-divider" />;
}
function Highlight({ children }) {
  return <div className="legal-highlight"><p className="legal-p" style={{ marginBottom: 0 }}>{children}</p></div>;
}

// ════════════════════════════════════════════════════════════
// PRIVACY POLICY
// ════════════════════════════════════════════════════════════
function PrivacyPolicy() {
  return (
    <div>
      <div className="legal-hero">
        <div className="legal-eyebrow">Legal Document</div>
        <div className="legal-title">Privacy Policy</div>
        <div className="legal-meta">Effective date: {EFFECTIVE_DATE} · {COMPANY}</div>
      </div>

      <Highlight>
        Your privacy matters to us. This policy explains what personal information we collect, why we collect it, how we protect it, and your rights under the Protection of Personal Information Act, 4 of 2013 (POPIA).
      </Highlight>

      <Divider />

      <div className="legal-section">
        <H2>1. Who We Are</H2>
        <P><strong>{COMPANY}</strong> is the responsible party for personal information processed through the InsightPro platform and website located at {WEBSITE}. Our registered address is {CITY}. You may contact us at <strong>{EMAIL}</strong> for any privacy-related queries.</P>
      </div>

      <div className="legal-section">
        <H2>2. Information We Collect</H2>
        <H3>2.1 Information you provide directly</H3>
        <UL items={[
          'Name, surname and email address when submitting a demo request or creating an account',
          'Company name, industry, number of branches and accounting system via our contact form',
          'Login credentials (email address only — passwords are managed by Firebase Authentication)',
          'Contact number if provided voluntarily',
        ]} />
        <H3>2.2 Information collected automatically</H3>
        <UL items={[
          'Browser type, device type and operating system',
          'IP address and approximate geographic location',
          'Pages visited, time spent on the platform and navigation patterns',
          'Session tokens and authentication activity logs',
        ]} />
        <H3>2.3 Client business data</H3>
        <P>InsightPro connects to your existing business systems (Sage, Pastel, POS, SQL databases) via an on-premises Power BI Gateway installed at your premises. <strong>Your raw transactional data never leaves your building.</strong> Only aggregated, anonymised metric data is transmitted to our cloud infrastructure for display on your dashboard.</P>
      </div>

      <div className="legal-section">
        <H2>3. How We Use Your Information</H2>
        <P>We use the information we collect to:</P>
        <UL items={[
          'Provide, operate and maintain the InsightPro platform',
          'Respond to demo requests and schedule demonstrations',
          'Create and manage your account and user access',
          'Send transactional emails (account invites, password resets, alert notifications)',
          'Improve and optimise platform features based on usage patterns',
          'Comply with legal obligations under South African law',
          'Protect the security and integrity of the platform',
        ]} />
        <P>We do <strong>not</strong> sell, rent or share your personal information with third parties for marketing purposes.</P>
      </div>

      <div className="legal-section">
        <H2>4. Legal Basis for Processing</H2>
        <P>Under POPIA, we process your personal information on the following grounds:</P>
        <UL items={[
          'Contract performance — to deliver the services you have requested',
          'Legitimate interest — to maintain platform security and improve our services',
          'Legal obligation — to comply with applicable South African laws and regulations',
          'Consent — where you have explicitly agreed to specific processing activities',
        ]} />
      </div>

      <div className="legal-section">
        <H2>5. Data Storage and Security</H2>
        <P>InsightPro is hosted on <strong>Google Cloud Platform (GCP)</strong> in the <strong>africa-south1 region (Johannesburg)</strong>. Your data is stored within South Africa's borders, consistent with POPIA's requirements for cross-border data transfers.</P>
        <P>We implement the following security measures:</P>
        <UL items={[
          'TLS encryption for all data in transit',
          'Firebase Authentication with custom claims for role-based access control',
          'Multi-tenant data isolation — each client\'s data is stored in a separate BigQuery dataset',
          'Audit logging of all administrative actions',
          'Secret Manager for secure storage of credentials and API keys',
          'Regular security reviews and access audits',
        ]} />
      </div>

      <div className="legal-section">
        <H2>6. Data Retention</H2>
        <P>We retain your personal information for as long as your account is active or as needed to provide services. Specifically:</P>
        <UL items={[
          'Active account data is retained for the duration of the service agreement',
          'Audit logs are retained for 90 days by default (configurable per client)',
          'Demo request data is retained for 12 months',
          'Upon account termination, personal data is deleted within 30 days unless legal obligations require longer retention',
        ]} />
      </div>

      <div className="legal-section">
        <H2>7. Your Rights Under POPIA</H2>
        <P>As a data subject, you have the right to:</P>
        <UL items={[
          'Access the personal information we hold about you',
          'Request correction of inaccurate or incomplete information',
          'Request deletion of your personal information (subject to legal retention requirements)',
          'Object to processing of your personal information',
          'Lodge a complaint with the Information Regulator of South Africa',
        ]} />
        <P>To exercise any of these rights, contact us at <strong>{EMAIL}</strong>. We will respond within 30 days.</P>
      </div>

      <div className="legal-section">
        <H2>8. Cookies and Analytics</H2>
        <P>InsightPro uses Microsoft Clarity for anonymised usage analytics (heatmaps and session recordings). No personally identifiable information is collected through these tools. We do not use advertising cookies or share analytics data with third parties.</P>
        <P>You may disable analytics by adjusting your browser settings. This will not affect your ability to use the platform.</P>
      </div>

      <div className="legal-section">
        <H2>9. Third-Party Services</H2>
        <P>InsightPro integrates with the following third-party services, each governed by their own privacy policies:</P>
        <UL items={[
          'Google Cloud Platform / Firebase — hosting, authentication and database',
          'Microsoft Power BI — data visualisation and business intelligence',
          'Microsoft Clarity — anonymous usage analytics',
          'xneelo — email hosting for @insightpro.co.za',
        ]} />
      </div>

      <div className="legal-section">
        <H2>10. Changes to This Policy</H2>
        <P>We may update this Privacy Policy from time to time. We will notify registered users of material changes via email. Continued use of the platform after notification constitutes acceptance of the updated policy.</P>
      </div>

      <div className="legal-contact-box">
        <div className="legal-contact-label">Contact — Privacy Queries</div>
        <div className="legal-contact-line"><strong>Information Officer:</strong> InsightPro (Pty) Ltd</div>
        <div className="legal-contact-line"><strong>Email:</strong> <a href={`mailto:${EMAIL}`}>{EMAIL}</a></div>
        <div className="legal-contact-line"><strong>Address:</strong> {CITY}</div>
        <div className="legal-contact-line" style={{ marginTop: 12 }}>
          <strong>Information Regulator of South Africa:</strong> <a href="https://www.justice.gov.za/inforeg/" target="_blank" rel="noopener noreferrer">www.justice.gov.za/inforeg</a>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TERMS OF USE
// ════════════════════════════════════════════════════════════
function TermsOfUse() {
  return (
    <div>
      <div className="legal-hero">
        <div className="legal-eyebrow">Legal Document</div>
        <div className="legal-title">Terms of Use</div>
        <div className="legal-meta">Effective date: {EFFECTIVE_DATE} · {COMPANY}</div>
      </div>

      <Highlight>
        Please read these Terms carefully before using the InsightPro platform. By accessing or using InsightPro, you agree to be bound by these Terms. If you do not agree, do not use the platform.
      </Highlight>

      <Divider />

      <div className="legal-section">
        <H2>1. Agreement</H2>
        <P>These Terms of Use ("Terms") constitute a legally binding agreement between you ("User", "Client") and <strong>{COMPANY}</strong> ("InsightPro", "we", "us") governing your use of the InsightPro platform available at {WEBSITE}.</P>
        <P>These Terms are governed by the laws of the Republic of South Africa, including the Electronic Communications and Transactions Act 25 of 2002 and the Protection of Personal Information Act 4 of 2013.</P>
      </div>

      <div className="legal-section">
        <H2>2. The Service</H2>
        <P>InsightPro is a Software-as-a-Service (SaaS) business intelligence platform that connects to your existing business systems and delivers real-time executive dashboards, KPI reporting and analytics for retail, wholesale, FMCG, furniture and manufacturing businesses.</P>
        <P>The platform operates on a consultant-assisted model. Access is provisioned by an authorised InsightPro consultant following a structured onboarding process. Self-registration is not currently available.</P>
      </div>

      <div className="legal-section">
        <H2>3. Account Access and Security</H2>
        <H3>3.1 User accounts</H3>
        <P>Access to InsightPro is granted on a per-user basis. Each user account is personal and non-transferable. You are responsible for maintaining the confidentiality of your login credentials.</P>
        <H3>3.2 Role-based access</H3>
        <P>Your access rights are determined by the user group and permissions assigned during onboarding. You agree not to attempt to access functionality, data or reports beyond your assigned permissions.</P>
        <H3>3.3 Account security</H3>
        <P>You must immediately notify InsightPro at <strong>{EMAIL}</strong> if you become aware of any unauthorised use of your account or any security breach.</P>
      </div>

      <div className="legal-section">
        <H2>4. Acceptable Use</H2>
        <P>You agree not to:</P>
        <UL items={[
          'Use the platform for any unlawful purpose or in violation of any applicable law',
          'Attempt to gain unauthorised access to any part of the platform or its infrastructure',
          'Reverse engineer, decompile or disassemble any part of the platform',
          'Share your login credentials with any other person',
          'Upload or transmit malicious code, viruses or harmful content',
          'Use the platform to process data you do not have the legal right to process',
          'Interfere with or disrupt the platform\'s infrastructure or other users\' access',
          'Attempt to extract, scrape or download data beyond your normal use of the platform',
        ]} />
      </div>

      <div className="legal-section">
        <H2>5. Data Ownership</H2>
        <P><strong>Your data remains yours.</strong> InsightPro does not claim ownership of any business data, financial data or operational data you access through the platform. We act as a data processor on your behalf.</P>
        <P>You represent and warrant that you have the legal right to process and display the data connected to InsightPro, including compliance with all applicable data protection laws.</P>
      </div>

      <div className="legal-section">
        <H2>6. Service Availability</H2>
        <P>InsightPro strives to maintain high platform availability. However, we do not guarantee uninterrupted access. The platform may be temporarily unavailable due to:</P>
        <UL items={[
          'Scheduled maintenance (we will endeavour to provide advance notice)',
          'Unplanned outages or infrastructure issues',
          'Third-party service disruptions (Google Cloud Platform, Microsoft Power BI)',
          'Connectivity issues between the on-premises gateway and cloud services',
        ]} />
        <P>InsightPro shall not be liable for any losses arising from platform unavailability.</P>
      </div>

      <div className="legal-section">
        <H2>7. Intellectual Property</H2>
        <P>The InsightPro platform, including its design, code, features, branding and documentation, is the intellectual property of <strong>{COMPANY}</strong> and is protected under South African intellectual property law.</P>
        <P>You are granted a limited, non-exclusive, non-transferable licence to use the platform for your internal business purposes only. No rights are transferred to you beyond this licence.</P>
      </div>

      <div className="legal-section">
        <H2>8. Confidentiality</H2>
        <P>Both parties agree to keep confidential all non-public information disclosed in connection with the use of InsightPro. InsightPro will not disclose your business data or configuration to any third party except as required by law or as necessary to provide the service.</P>
      </div>

      <div className="legal-section">
        <H2>9. Limitation of Liability</H2>
        <P>To the maximum extent permitted by South African law, InsightPro shall not be liable for any indirect, incidental, special, consequential or punitive damages arising from your use of the platform, including but not limited to loss of profits, loss of data or business interruption.</P>
        <P>InsightPro's total liability to you for any claim arising from these Terms shall not exceed the fees paid by you in the three months preceding the claim.</P>
      </div>

      <div className="legal-section">
        <H2>10. Termination</H2>
        <P>Either party may terminate access to the platform upon written notice. InsightPro reserves the right to immediately suspend or terminate access if you breach these Terms.</P>
        <P>Upon termination, your access will be revoked and your data will be deleted within 30 days in accordance with our Privacy Policy.</P>
      </div>

      <div className="legal-section">
        <H2>11. Modifications</H2>
        <P>InsightPro reserves the right to modify these Terms at any time. We will notify registered users of material changes via email with at least 14 days' notice. Continued use of the platform after the effective date of changes constitutes acceptance.</P>
      </div>

      <div className="legal-section">
        <H2>12. Governing Law and Disputes</H2>
        <P>These Terms are governed by the laws of the Republic of South Africa. Any disputes arising from these Terms shall be subject to the jurisdiction of the courts of KwaZulu-Natal, South Africa.</P>
        <P>We encourage resolution of disputes through direct communication before initiating legal proceedings. Contact us at <strong>{EMAIL}</strong>.</P>
      </div>

      <div className="legal-contact-box">
        <div className="legal-contact-label">Contact — Legal Queries</div>
        <div className="legal-contact-line"><strong>Company:</strong> {COMPANY}</div>
        <div className="legal-contact-line"><strong>Email:</strong> <a href={`mailto:${EMAIL}`}>{EMAIL}</a></div>
        <div className="legal-contact-line"><strong>Address:</strong> {CITY}</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// POPIA COMPLIANCE STATEMENT
// ════════════════════════════════════════════════════════════
function PopiaStatement() {
  return (
    <div>
      <div className="legal-hero">
        <div className="legal-eyebrow">Legal Document</div>
        <div className="legal-title">POPIA Compliance Statement</div>
        <div className="legal-meta">Effective date: {EFFECTIVE_DATE} · {COMPANY}</div>
      </div>

      <Highlight>
        InsightPro (Pty) Ltd is committed to full compliance with the Protection of Personal Information Act, 4 of 2013 (POPIA). This statement outlines how we fulfil our obligations as a responsible party and operator under POPIA.
      </Highlight>

      <Divider />

      <div className="legal-section">
        <H2>1. Our Commitment</H2>
        <P><strong>{COMPANY}</strong> recognises the fundamental right to privacy and is committed to processing personal information lawfully, reasonably and with appropriate safeguards. We act as both a <strong>responsible party</strong> (for our own users and prospects) and as an <strong>operator</strong> (processing client business data on behalf of our clients).</P>
      </div>

      <div className="legal-section">
        <H2>2. Information Officer</H2>
        <P>In accordance with section 55 of POPIA, {COMPANY} has designated an Information Officer responsible for overseeing compliance with POPIA. The Information Officer can be contacted at <strong>{EMAIL}</strong>.</P>
      </div>

      <div className="legal-section">
        <H2>3. The Eight Conditions for Lawful Processing</H2>
        <P>InsightPro complies with all eight conditions for lawful processing under POPIA:</P>
        <H3>Accountability</H3>
        <P>We take responsibility for ensuring that all personal information processing complies with POPIA's requirements and have implemented appropriate policies and procedures.</P>
        <H3>Processing Limitation</H3>
        <P>We only collect personal information that is adequate, relevant and not excessive for the identified purpose. Data is collected with the knowledge and consent of the data subject where required.</P>
        <H3>Purpose Specification</H3>
        <P>Personal information is collected for a specific, explicitly defined purpose: to provide access to and operation of the InsightPro platform. Information is not processed for any incompatible secondary purpose.</P>
        <H3>Further Processing Limitation</H3>
        <P>We do not further process personal information in a manner incompatible with the original purpose of collection. We do not sell or share personal information with third parties for commercial gain.</P>
        <H3>Information Quality</H3>
        <P>We take reasonable steps to ensure that personal information is complete, accurate and up to date. Users may update their information by contacting us at {EMAIL}.</P>
        <H3>Openness</H3>
        <P>We maintain this public POPIA Compliance Statement and Privacy Policy. Data subjects are informed about the collection of their personal information and the purpose thereof.</P>
        <H3>Security Safeguards</H3>
        <P>We implement appropriate technical and organisational measures to protect personal information against loss, damage, unauthorised access, interference, modification or disclosure. These measures include TLS encryption, role-based access control, audit logging and infrastructure hosted within South Africa on Google Cloud Platform.</P>
        <H3>Data Subject Participation</H3>
        <P>Data subjects have the right to access, correct and delete their personal information. Requests can be submitted to {EMAIL} and will be processed within 30 days.</P>
      </div>

      <div className="legal-section">
        <H2>4. Data Localisation</H2>
        <P>InsightPro's cloud infrastructure is hosted in Google Cloud Platform's <strong>africa-south1 region (Johannesburg)</strong>. All client data and personal information is stored within South Africa's borders, consistent with POPIA's requirements regarding the transfer of personal information outside the Republic.</P>
        <P>Where third-party services process data outside South Africa (such as Microsoft services), we ensure adequate protection measures are in place consistent with POPIA's cross-border transfer requirements.</P>
      </div>

      <div className="legal-section">
        <H2>5. On-Premises Data Architecture</H2>
        <P>InsightPro's architecture is designed with POPIA compliance as a core principle. Client raw transactional and financial data remains on the client's own premises and is never transmitted to InsightPro's cloud infrastructure in raw form. Only aggregated, computed metrics are transmitted for dashboard display. This architecture minimises the personal information processed in the cloud and reduces compliance risk for our clients.</P>
      </div>

      <div className="legal-section">
        <H2>6. Operator Agreements</H2>
        <P>Where InsightPro processes personal information as an operator on behalf of our clients, we do so only under the client's instructions and in accordance with written operator agreements. These agreements ensure that client data is processed only for the agreed purpose and with appropriate safeguards.</P>
      </div>

      <div className="legal-section">
        <H2>7. Security Incidents</H2>
        <P>In the event of a security compromise involving personal information, InsightPro will notify affected data subjects and the Information Regulator as soon as reasonably possible after becoming aware of the compromise, in accordance with section 22 of POPIA.</P>
      </div>

      <div className="legal-section">
        <H2>8. Contact the Information Regulator</H2>
        <P>If you believe your rights under POPIA have been violated and are not satisfied with our response, you have the right to lodge a complaint with the Information Regulator of South Africa:</P>
        <div className="legal-contact-box">
          <div className="legal-contact-label">Information Regulator of South Africa</div>
          <div className="legal-contact-line"><strong>Website:</strong> <a href="https://www.justice.gov.za/inforeg/" target="_blank" rel="noopener noreferrer">www.justice.gov.za/inforeg</a></div>
          <div className="legal-contact-line"><strong>Email:</strong> <a href="mailto:inforeg@justice.gov.za">inforeg@justice.gov.za</a></div>
        </div>
      </div>

      <div className="legal-contact-box" style={{ marginTop: 32 }}>
        <div className="legal-contact-label">Contact — POPIA Queries</div>
        <div className="legal-contact-line"><strong>Information Officer:</strong> {COMPANY}</div>
        <div className="legal-contact-line"><strong>Email:</strong> <a href={`mailto:${EMAIL}`}>{EMAIL}</a></div>
        <div className="legal-contact-line"><strong>Address:</strong> {CITY}</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════
export default function LegalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState('privacy');

  // Allow deep-linking e.g. /legal?tab=terms
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t && ['privacy', 'terms', 'popia'].includes(t)) setTab(t);
  }, [location.search]);

  useEffect(() => {
    const el = document.createElement('style');
    el.id = 'legal-styles';
    if (!document.getElementById('legal-styles')) document.head.appendChild(el);
    el.textContent = css;
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);

  // Scroll to top on tab change
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [tab]);

  return (
    <div className="legal-root">
      <nav className="legal-nav">
        <div className="legal-logo" onClick={() => navigate('/home')}>
          <div className="legal-logo-mark"><span>IP</span></div>
          <div className="legal-brand">InsightPro<sup>™</sup></div>
        </div>
        <button className="legal-nav-back" onClick={() => navigate('/home')}>← Back to Home</button>
      </nav>

      <div className="legal-body">
        <div className="legal-tabs">
          {[
            ['privacy', 'Privacy Policy'],
            ['terms',   'Terms of Use'],
            ['popia',   'POPIA Statement'],
          ].map(([key, label]) => (
            <button
              key={key}
              className={`legal-tab${tab === key ? ' active' : ''}`}
              onClick={() => setTab(key)}
            >{label}</button>
          ))}
        </div>

        {tab === 'privacy' && <PrivacyPolicy />}
        {tab === 'terms'   && <TermsOfUse />}
        {tab === 'popia'   && <PopiaStatement />}
      </div>

      <footer className="legal-footer">
        <div className="legal-footer-copy">© 2026 {COMPANY} · All rights reserved</div>
        <div className="legal-footer-copy">{WEBSITE}</div>
      </footer>
    </div>
  );
}
